import { useQuery } from '@tanstack/react-query';
import { Deal } from '../../types';
import { fetchDeals, toBasicDeal } from '../../services/dealService';

/**
 * Query keys for deal cache management
 */
export const dealsKeys = {
  all: ['deals'] as const,
  forUser: (requestId?: string) => [...dealsKeys.all, 'forUser', requestId ?? 'new'] as const,
};

interface UseDealsQueryOptions {
  /** Request ID - if provided, verifies user owns the request. Optional for new requests. */
  requestId?: string;
  /** Whether the query should execute */
  enabled?: boolean;
}

/**
 * Session-level cached hook for deal registrations from Snowflake
 *
 * The email is extracted from the JWT token on the server side,
 * so no email parameter is needed from the client.
 *
 * Features:
 * - Fetches once per session (staleTime: Infinity)
 * - Manual refresh via refetch()
 * - In-memory cache clears on app restart
 * - Reduces costly Snowflake/SQL Server requests
 *
 * @example
 * const { deals, isLoading, isFetching, refetch } = useDealsQuery({
 *   requestId: existingRequestId, // optional - for ownership verification
 * });
 *
 * // Refresh button
 * <Button label="Refresh" onPress={() => refetch()} disabled={isFetching} />
 */
export const useDealsQuery = ({ requestId, enabled = true }: UseDealsQueryOptions) => {
  const query = useQuery<Deal[], Error>({
    queryKey: dealsKeys.forUser(requestId),
    queryFn: async () => {
      const response = await fetchDeals(requestId);
      return response.deals.map(toBasicDeal);
    },

    // Session-level cache configuration
    staleTime: Infinity, // Never auto-refetch - data refreshes 1-2x daily
    gcTime: 60 * 60 * 1000, // Keep in memory for 1 hour
    refetchOnMount: false, // Use cache when modal reopens
    refetchOnWindowFocus: false, // No background refetch

    // Query is enabled unless explicitly disabled
    enabled,

    meta: {
      errorMessage: 'Failed to fetch deals from Snowflake.',
    },
  });

  return {
    deals: query.data ?? [],
    isLoading: query.isLoading, // True on first fetch only
    isFetching: query.isFetching, // True during any fetch (initial + refresh)
    error: query.error,
    refetch: query.refetch, // Manual refresh trigger
    dataUpdatedAt: query.dataUpdatedAt, // Timestamp of last successful fetch (ms)
  };
};
